import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { useUiDensity } from "../hooks/useDensity";

export type CardTone = "default" | "danger" | "warning" | "sunken" | "brand";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Card clicável: hover não sobe sombra (isso pisca e cansa quem olha 20x
   * por dia) — ganha um contorno de 1px (DESIGN.md › Elevation).
   */
  interactive?: boolean;
  /**
   * - `default`: painel branco sobre o canvas cinza, raio 16, sem borda e sem sombra.
   * - `sunken`: bloco cinza DENTRO de um painel (em vez de card dentro de card).
   * - `brand`: o ÚNICO KPI em destaque da tela (violeta + brilho).
   * - `danger`/`warning`: contorno de estado (ERROR do monitorador, seção 8).
   */
  tone?: CardTone;
  /** Contorno de 1px — use quando o card está sobre branco, não sobre o canvas. */
  bordered?: boolean;
  /** `false` quando o filho controla o padding (tabela). */
  padded?: boolean;
  /** `md` = 20px, `sm` = 16px. */
  size?: "sm" | "md";
}

const TONE: Record<CardTone, string> = {
  default: "bg-surface text-foreground",
  sunken: "bg-surface-subtle text-foreground ring-1 ring-inset ring-border rounded-md",
  brand: "bg-primary text-primary-foreground shadow-brand",
  danger: "bg-surface text-foreground ring-1 ring-inset ring-red-100",
  warning: "bg-surface text-foreground ring-1 ring-inset ring-yellow-100",
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    className,
    interactive = false,
    tone = "default",
    bordered = false,
    padded = true,
    size = "md",
    ...props
  },
  ref,
) {
  // I3: no compacto o card perde um degrau de padding (o conteúdo não encolhe).
  const compact = useUiDensity() === "compact";
  return (
    <div
      ref={ref}
      data-tone={tone}
      className={cn(
        // painéis no mesmo plano não têm sombra: branco sobre cinza separa sozinho
        "min-w-0 rounded-lg",
        TONE[tone],
        bordered && "ring-1 ring-inset ring-border",
        padded && !compact && (size === "sm" ? "p-3 md:p-4" : "p-4 md:p-5"),
        padded && compact && (size === "sm" ? "p-2.5 md:p-3" : "p-3 md:p-4"),
        interactive &&
          "cursor-pointer transition-[box-shadow,background-color] duration-[var(--duration-micro)] hover:ring-1 hover:ring-inset hover:ring-border-strong focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/40",
        className,
      )}
      {...props}
    />
  );
});

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Atalho para `<CardTitle>`. */
  title?: ReactNode;
  /** Atalho para `<CardDescription>`. */
  description?: ReactNode;
  /** Ação alinhada à direita (botão ghost/ícone). */
  action?: ReactNode;
}

export function CardHeader({
  className,
  title,
  description,
  action,
  children,
  ...props
}: CardHeaderProps) {
  return (
    <div
      className={cn("mb-4 flex items-start justify-between gap-3", className)}
      {...props}
    >
      <div className="min-w-0 flex flex-col gap-0.5">
        {title ? <CardTitle>{title}</CardTitle> : null}
        {description ? <CardDescription>{description}</CardDescription> : null}
        {children}
      </div>
      {action ? <div className="flex shrink-0 gap-2">{action}</div> : null}
    </div>
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("truncate text-md", className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-xs text-muted-foreground", className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("min-w-0", className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mt-4 flex items-center gap-2 border-t border-border pt-4", className)}
      {...props}
    />
  );
}
